import { useState } from "react";
import { signInWithEmailAndPassword, signOut } from "firebase/auth";
import { toast } from "sonner";

import { useAuth } from "@/auth/AuthProvider";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { getFirebaseAuth } from "@/lib/firebase";

export function KioskLoginPage() {
  const { user } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    try {
      await signInWithEmailAndPassword(getFirebaseAuth(), email, password);
      toast.success("Kiosk signed in");
    } catch (err: any) {
      toast.error("Sign-in failed", {
        description: err?.message || "Unknown error",
      });
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="mx-auto flex min-h-screen max-w-lg flex-col justify-center gap-6 p-8">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">
            Memorix Kiosk
          </h1>
          <p className="mt-2 text-white/70">
            Sign in to activate this kiosk device.
          </p>
        </div>

        <Card className="border-white/10 bg-white/5 text-white">
          <CardHeader>
            <CardTitle>Kiosk Login</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {user ? (
              <div className="space-y-4 text-sm">
                <div>
                  Signed in as <span className="font-medium">{user.email}</span>
                </div>
                <div className="flex gap-3">
                  <Button className="flex-1" size="lg" asChild>
                    <a href="/kiosk">Go to kiosk</a>
                  </Button>
                  <Button
                    className="flex-1"
                    size="lg"
                    variant="outline"
                    onClick={() => signOut(getFirebaseAuth())}
                  >
                    Sign out
                  </Button>
                </div>
              </div>
            ) : (
              <form className="space-y-5" onSubmit={onSubmit}>
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-white/80">
                    Email
                  </Label>
                  <Input
                    id="email"
                    className="h-12 border-white/15 bg-white/5 text-white placeholder:text-white/40"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="username"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-white/80">
                    Password
                  </Label>
                  <Input
                    id="password"
                    className="h-12 border-white/15 bg-white/5 text-white placeholder:text-white/40"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                    required
                  />
                </div>
                <Button
                  type="submit"
                  className="h-12 w-full text-base"
                  size="lg"
                  disabled={loading}
                >
                  {loading ? "Signing in…" : "Sign in"}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
